import { useEffect, useRef, useState } from "react";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import { productGroups, productHref, products } from "../data/products";

const PRODUCT_URL = "https://connect.cullath.com/";

export function Brand({ homeHref = "/" }) {
  return (
    <a className="brand" href={homeHref} aria-label="Cullath home">
      <span className="brand-mark">
        <img src="/1.png" alt="" aria-hidden="true" />
      </span>
      <span>Cullath</span>
    </a>
  );
}

function HeroTopNavBrand({ homeHref = "/" }) {
  return (
    <a className="hero-top-nav-brand" href={homeHref} aria-label="Cullath home">
      <img src="/cullath-nav-logo.png" alt="Cullath logo" />
    </a>
  );
}

function TopNavBrand({ homeHref = "/" }) {
  return (
    <a className="top-nav-brand" href={homeHref} aria-label="Cullath home">
      <img
        src="/1.png"
        alt="Cullath — Where Work Orbits"
      />
    </a>
  );
}

function ProductMenuContent({ onNavigate }) {
  return (
    <>
      <div className="mega-menu-heading">
        <div>
          <span>THE CULLATH ORBIT</span>
          <strong>Products built to work together.</strong>
        </div>
      </div>
      <div className="mega-menu-grid">
        {productGroups.flatMap((group) => group.products).map((slug) => {
          const product = products[slug];
          const Icon = product.icon;
          return (
            <a className={`mega-product-link mega-product-link--${slug}`} href={productHref(slug)} key={slug} onClick={onNavigate}>
              <span className="mega-product-icon"><Icon size={20} /></span>
              <span className="mega-product-copy">
                <strong>{product.shortName || product.name}{product.badge.includes("BETA") && <em>Beta</em>}</strong>
                <small>{product.menuDescription}</small>
              </span>
              <span className="mega-product-action" aria-hidden="true"><ArrowRight size={16} /></span>
            </a>
          );
        })}
      </div>
    </>
  );
}

export default function SiteHeader({ isProductPage = false, isHeroPage = false, lightProductHero = false }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const productsMenuRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (productsMenuRef.current && !productsMenuRef.current.contains(event.target)) {
        setProductsOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);
    return () => document.removeEventListener("pointerdown", handleOutsideClick);
  }, []);

  const closeMenu = () => {
    setMobileOpen(false);
    setMobileProductsOpen(false);
    setProductsOpen(false);
  };

  return (
    <header className={`site-header ${isHeroPage ? "site-header--hero" : ""} ${lightProductHero ? "site-header--light-product" : ""} ${scrolled ? "site-header--scrolled" : ""}`}>
      <div className="nav-container">
        {isHeroPage ? <HeroTopNavBrand /> : <TopNavBrand />}
        <nav className="desktop-nav" aria-label="Primary navigation">
          <div ref={productsMenuRef} className={`nav-products ${productsOpen ? "is-open" : ""}`}>
            <button
              type="button"
              className="nav-products-button"
              onClick={() => setProductsOpen((value) => !value)}
              aria-expanded={productsOpen}
              aria-haspopup="true"
            >
              Products <ChevronDown size={14} />
            </button>
            <div className="products-mega" role="menu">
              <ProductMenuContent onNavigate={() => setProductsOpen(false)} />
            </div>
          </div>
          <a href="/about-us">About Us</a>
          <a href="/pricing">Pricing</a>
          <a href="/contact-us">Contact Us</a>
        </nav>
        <div className="nav-actions">
          <a className="primary-button primary-button--small" href={PRODUCT_URL} target="_blank" rel="noopener noreferrer">Explore product <ArrowRight size={15} /></a>
        </div>
        <button
          className="mobile-menu-button"
          onClick={() => setMobileOpen((value) => !value)}
          aria-label="Toggle navigation"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X size={21} /> : <Menu size={21} />}
        </button>
      </div>

      <div className={`mobile-nav ${mobileOpen ? "mobile-nav--open" : ""}`}>
        <button
          className={`mobile-products-toggle ${mobileProductsOpen ? "active" : ""}`}
          onClick={() => setMobileProductsOpen((value) => !value)}
          aria-expanded={mobileProductsOpen}
          type="button"
        >
          <span>Products</span><ChevronDown size={17} />
        </button>
        <div className={`mobile-products-panel ${mobileProductsOpen ? "mobile-products-panel--open" : ""}`}>
          {productGroups.map((group) => (
            <div className="mobile-product-group" key={group.title}>
              <strong>{group.title}</strong>
              {group.products.map((slug) => {
                const product = products[slug];
                const Icon = product.icon;
                return (
                  <a href={productHref(slug)} onClick={closeMenu} key={slug}>
                    <span><Icon size={16} /></span>
                    <div><b>{product.shortName || product.name}</b><small>{product.menuDescription}</small></div>
                  </a>
                );
              })}
            </div>
          ))}
        </div>
        <a href="/about-us" onClick={closeMenu}>About Us</a>
        <a href="/pricing" onClick={closeMenu}>Pricing</a>
        <a href="/contact-us" onClick={closeMenu}>Contact Us</a>
        <a className="primary-button" href={PRODUCT_URL} target="_blank" rel="noopener noreferrer" onClick={closeMenu}>Explore product <ArrowRight size={16} /></a>
      </div>
    </header>
  );
}
